import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PiNewspaperDuotone } from "react-icons/pi";

import api from "../../helpers/Api";
import Loading from "../../components/Loading";
import { SkeletonNewsPage } from "../../components/Skeleton";
import MessageError from "../../components/MessageError";

export function Render({ news }) {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const routeChange = async (id) => {
        setLoading(true);
        navigate(`/news/detail/${id}`);
    };

    const featuredNews = news.filter((item) => item.destacado);
    const normalNews = news.filter((item) => !item.destacado);

    const getImageUrl = (item) => {
        if (item.imagenPrincipalId) return `${api.defaults.baseURL}/news-image-proxy?id=${item.imagenPrincipalId}`;
        if (item.imagenPrincipal) return item.imagenPrincipal;
        return null;
    };

    const NewsCard = ({ item, featured = false }) => {
        const img = getImageUrl(item);
        return (
            <div className="group flex flex-col bg-[#16151c] border border-white/10 hover:border-brand/30 rounded-2xl overflow-hidden transition-all duration-300 shadow-xl">
                <div className={`relative overflow-hidden bg-[#0f0e17] ${featured ? "h-56" : "h-44"}`}>
                    {img ? (
                        <img
                            src={img}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => { e.target.style.display = "none"; }}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <PiNewspaperDuotone className="text-gray-600 text-5xl" />
                        </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#16151c] via-transparent to-transparent opacity-70" />
                    {featured && (
                        <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider font-mono px-2.5 py-1 rounded-full bg-brand/20 border border-brand/40 text-brand">
                            Destacado
                        </span>
                    )}
                </div>

                <div className="flex flex-col flex-1 p-5 gap-3">
                    <h5 className={`font-display font-bold text-white leading-tight line-clamp-2 ${featured ? "text-xl" : "text-base"}`}>
                        {item.title}
                    </h5>
                    <p className="text-gray-400 text-xs leading-relaxed line-clamp-3">
                        {item.content || item.descripcion || "Sin descripción disponible."}
                    </p>
                    <div className="mt-auto pt-2">
                        <button onClick={() => routeChange(item.id)} className="event-one__btn thm-btn w-full">
                            Leer más
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-[#0f0e17] min-h-screen py-14 px-4">
            <div className="max-w-7xl mx-auto">

                <div className="text-center mb-10">
                    <span className="text-brand font-semibold text-sm uppercase tracking-widest font-mono">Blog</span>
                    <h2 className="text-white font-bold text-3xl sm:text-4xl mt-2 font-display">Noticias</h2>
                    <p className="text-gray-400 mt-2 text-sm">Entérate de las últimas noticias</p>
                </div>

                {featuredNews.length > 0 && (
                    <div className="mb-12">
                        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3 font-display">
                            <span className="w-1 h-6 bg-brand rounded-full" />
                            Noticias Destacadas
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {featuredNews.map((item, i) => <NewsCard key={i} item={item} featured />)}
                        </div>
                    </div>
                )}

                {featuredNews.length > 0 && normalNews.length > 0 && (
                    <hr className="border-white/10 my-10" />
                )}

                {normalNews.length > 0 && (
                    <div>
                        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3 font-display">
                            <span className="w-1 h-6 bg-brand rounded-full" />
                            Todas las Noticias
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {normalNews.map((item, i) => <NewsCard key={i} item={item} />)}
                        </div>
                    </div>
                )}

                {news.length === 0 && (
                    <div className="text-center py-20">
                        <PiNewspaperDuotone className="text-gray-600 text-5xl mx-auto mb-4" />
                        <p className="text-gray-400 text-lg">No hay noticias disponibles.</p>
                    </div>
                )}
            </div>

            {loading && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                    <Loading text="Cargando noticia..." />
                </div>
            )}
        </div>
    );
}

export default function AllNews() {
    const [news, setNews] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const newsResponse = await api.get(`/NewsEvent`);
                const newsWithImages = newsResponse.data.map((item) => {
                    const img = item.images?.[0];
                    return {
                        ...item,
                        content: item.content_new,
                        descripcion: item.content_new,
                        imagenPrincipal: img?.image_url || null,
                        imagenPrincipalId: img?.id || null,
                    };
                });
                setNews(newsWithImages);
            } catch {
                setError(true);
            }
        };
        fetchData();
    }, []);

    if (error) return <MessageError title="Error al cargar noticias." description="Intenta de nuevo." />;
    if (!news) return <SkeletonNewsPage />;
    return <Render news={news} />;
}
